/* eslint-disable @typescript-eslint/ban-ts-comment */
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import {
  Button,
  Popover,
  PopoverWC,
  Select,
  SelectOption,
  Spacer,
  Stack,
  TextBody,
  TextSubtitle,
} from '@pypestream/design-system';

import { SelectPlaceholder } from './select-placeholder';
import {
  defaultColumnWidths,
  NodeColumns,
  TableRow,
  tableStore,
} from '../../stores/table-store';
import { useLoadingState } from '../../hooks';

interface NodeTypeCellProps {
  rowId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnId?: string | number | symbol | any;
  testId?: string;
}

// Define the valid node types as a const object to use as a type
// const nodeTypeColors = {
//   decision: '#ffd700',
//   action: '#e0e0e0', // Added action type since it's in the select options
// } as const;

export const NodeTypeCell: React.FC<NodeTypeCellProps> = observer((props) => {
  const { rowId, testId } = props;
  const [isSelectRendered, setIsSelectRendered] = useState(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const selectRef = useRef<React.ComponentRef<typeof Select>>(null);
  const popoverRef = useRef<PopoverWC | null>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLElement | null>(null);
  const columnSize =
    tableStore.columnsSizes?.[NodeColumns.nodeType] ||
    defaultColumnWidths[NodeColumns.nodeType];

  const isLoading = useLoadingState();
  const isProcessing = isLoading || tableStore.isValidating;

  const nodeType = tableStore.getValue(rowId, NodeColumns.nodeType);

  const renderedValue =
    nodeType === 'Decision' ||
    nodeType === 'decision' ||
    nodeType === 'd' ||
    nodeType === 'D'
      ? Tenancy_Node_Type_Enum_Ps_Enum.D
      : Tenancy_Node_Type_Enum_Ps_Enum.A;

  const displayValue =
    renderedValue === Tenancy_Node_Type_Enum_Ps_Enum.D ? 'D' : 'A';

  useEffect(() => {
    if (isSelectRendered && selectRef.current) {
      // Use a longer delay to ensure the component is fully rendered
      setTimeout(() => {
        if (selectRef.current && typeof selectRef.current.show === 'function') {
          selectRef.current.show();
          if (selectRef.current.focus) {
            selectRef.current.focus();
          }
        }
      }, 50);
    }
  }, [isSelectRendered]);

  const closePopover = useCallback(() => {
    if (popoverRef.current && typeof popoverRef.current.hide === 'function') {
      popoverRef.current.hide();
    }
  }, []);

  const selectParentCell = useCallback(() => {
    const cellId = `${rowId}-${NodeColumns.nodeType}`;
    tableStore.setKeyboardNav(true);
    tableStore.isCellBeingEdited = false;
    tableStore.deselectAllRows();
    tableStore.deselectAllCells();
    tableStore.selectCell({
      cellId,
      columnId: NodeColumns.nodeType,
      rowId,
    });
    tableStore.setSelectedStartCell({
      cellId,
      columnId: NodeColumns.nodeType,
      rowId,
    });
    const td = (selectRef.current as HTMLElement)?.closest('td');
    td?.focus?.({ preventScroll: true });
  }, [rowId]);

  const closeSelect = useCallback(() => {
    if (selectRef.current && typeof selectRef.current.hide === 'function') {
      selectRef.current.hide();

      closePopover();
    }
  }, [closePopover]);

  // Add event listener for the custom ps-option-select event
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleOptionSelect = (event: CustomEvent) => {
      const { value } = event.detail;

      if (value === renderedValue) {
        closeSelect();
        setTimeout(() => selectParentCell(), 100);
        return;
      }

      setPendingValue(value);

      // Show the popover after a small delay to ensure the select is rendered
      setTimeout(() => {
        if (popoverRef.current && selectContainerRef.current) {
          // Set the trigger to the select container
          popoverRef.current.trigger = selectContainerRef.current;
          popoverRef.current.active = true;
        }
      }, 0);
    };

    // Store reference for cleanup
    const element: HTMLElement = selectElement as HTMLElement;
    element.addEventListener(
      'ps-option-select',
      handleOptionSelect as EventListener
    );

    return () => {
      element.removeEventListener(
        'ps-option-select',
        handleOptionSelect as EventListener
      );
    };
  }, [
    closeSelect,
    isSelectRendered,
    pendingValue,
    renderedValue,
    selectParentCell,
  ]);

  // Focus the Confirm button when the confirmation popover opens (from both click and keyboard)
  useEffect(() => {
    if (!pendingValue) return;

    const timeoutId = setTimeout(() => {
      confirmButtonRef.current?.focus();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [pendingValue]);

  if (tableStore.isEditing()) {
    if (!isSelectRendered) {
      return (
        <SelectPlaceholder
          testId={`${testId}-placeholder`}
          onOpen={() => setIsSelectRendered(true)}
          ariaLabel="Open node type selection"
          className={`c-editor-table__node-type-cell c-editor-table__node-type-cell--${renderedValue}`}
        >
          <span className="c-editor-table__node-type-cell-tag">
            {displayValue}
          </span>
        </SelectPlaceholder>
      );
    }

    const popoverId = `node-type-popover-${rowId}`;

    const handleConfirm = () => {
      if (pendingValue && selectRef.current) {
        // Update the value in the select
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectElement = selectRef.current as any;
        if (selectElement && typeof selectElement.setValue === 'function') {
          selectElement.setValue(pendingValue);
        }

        // Update the store
        const nextType = pendingValue as TableRow['nodeType'];
        tableStore.updateRowType({ rowId, newRowType: nextType });

        closeSelect();

        // Reset pending value
        setPendingValue(null);

        // Parent cell selection happens via Popover onClose
      }
    };

    const handleCancel = () => {
      closeSelect();
      setPendingValue(null);
    };

    const handleSelectClose = () => {
      setTimeout(() => {
        tableStore.isCellBeingEdited = false;
      }, 100);

      setTimeout(() => {
        if (selectRef.current?.open) return;

        if (isSelectRendered) {
          // Reset to fake select after closing
          setIsSelectRendered(false);
        }
      }, 2000);
    };

    return (
      <div
        ref={selectContainerRef}
        className={`c-editor-table__node-type-cell  c-editor-table__node-type-cell--${renderedValue}`}
      >
        <Select
          ref={selectRef}
          value={renderedValue}
          test-id={testId}
          placement="bottom-start"
          requireConfirmation
          // @ts-ignore WC prop confirmationPopoverId - prevents closing dropdown when clicking inside confirmation popover
          confirmationPopoverId={popoverId}
          onclose={handleSelectClose}
          onmouseleave={() => {
            setTimeout(() => {
              if (selectRef.current?.open) return;

              if (isSelectRendered) {
                // Reset to fake select after closing
                setIsSelectRendered(false);
              }
            }, 500);
          }}
          size="small"
          variant="outlined"
          className="c-editor-table__node-type-cell-select"
        >
          <span className="c-editor-table__node-type-cell-tag" slot="prefix">
            {displayValue}
          </span>
          <SelectOption value="d">Decision</SelectOption>
          <SelectOption value="a">Action</SelectOption>
        </Select>
        <Popover
          test-id={`${testId}-confirmation-popover`}
          id={popoverId}
          hasArrow={false}
          placement="right-end"
          // @ts-ignore
          ref={popoverRef}
          size="small"
          width="auto"
          skidding={100}
          distance={columnSize > 120 ? 20 : 119 - columnSize + 20}
          trigger={selectContainerRef.current || undefined}
          disableTriggerListeners
          onClose={() => {
            setTimeout(() => selectParentCell(), 100);
          }}
        >
          <TextSubtitle size="small">Change Node Type?</TextSubtitle>
          <Spacer size="2xsmall" />
          <TextBody
            variant="secondary"
            size="small"
            style={{ display: 'block', width: 269 }}
          >
            {pendingValue && pendingValue !== renderedValue && (
              <>
                Warning: Since you are changing the Node Type from{' '}
                {renderedValue === Tenancy_Node_Type_Enum_Ps_Enum.D ? (
                  <strong>Decision</strong>
                ) : (
                  <strong>Action</strong>
                )}{' '}
                to{' '}
                {pendingValue === Tenancy_Node_Type_Enum_Ps_Enum.D ? (
                  <strong>Decision</strong>
                ) : (
                  <strong>Action</strong>
                )}
                , all data within the{' '}
                {renderedValue === Tenancy_Node_Type_Enum_Ps_Enum.D &&
                pendingValue === Tenancy_Node_Type_Enum_Ps_Enum.A
                  ? 'blue Decision'
                  : 'green Action'}{' '}
                columns will be lost. To avoid this, simply create a new node.
              </>
            )}
          </TextBody>
          <Spacer size="xsmall" />
          <Stack
            direction="row"
            justifyContent="space-between"
            gutter="xsmall"
            nowrap
          >
            <Button
              variant="secondary"
              width="full"
              onClick={handleCancel}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  handleCancel();
                }
              }}
            >
              Cancel
            </Button>
            <Button
              ref={(el) => {
                confirmButtonRef.current = el as HTMLElement | null;
              }}
              variant="danger"
              width="full"
              onClick={handleConfirm}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
            >
              Confirm
            </Button>
          </Stack>
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={`c-editor-table__node-type-cell c-editor-table__node-type-cell--${nodeType}`}
    >
      <div className="c-editor-table__tag">{nodeType}</div>
    </div>
  );
});
